import { memo, useEffect, useState } from 'react';
import { FeatureGroup, LayersControl, useMap } from 'react-leaflet';

import L from 'leaflet';

import { addStartEndIcons } from '../../helpers/map';

import { GEOMETRY_TYPE } from '../../constants/map';

import AnimalDetails from '../../components/TrackLivestock/AnimalDetails';

import BaseLayers from './BaseLayers';
import AppMapContainer from './Container';
import CustomLayers from './CustomLayers';
import MapTrackingFeature from './MapTrackingFeature';
import { DEFAULT_LINE_COLOR, LAYERS_CONTROL_POSITION, LAYER_NAME, ZOOM_MAX } from './constants';
import useFeatures from './useFeatures';

const { Overlay } = LayersControl;

function TrackingLayer({ animal, grazing, mapData, tracking, onAnimalClick }) {
  const map = useMap();

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [tracks, setTracks] = useState(null);
  const [state, setState] = useState({
    features: { features: [] },
    features_master: {},
    features_sites: { features: [] }
  });

  const handleFullScreenChange = () => {
    setIsFullScreen(!!document.fullscreenElement);
  };

  useEffect(() => {
    tick();

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    tick();
  }, [animal, tracking]);

  function calculateBounds(features) {
    if (!features?.length) return;

    const bounds = L.latLngBounds();

    features.forEach(feature => {
      if (feature.geometry && feature.geometry.coordinates) {
        const coordinates =
          feature.geometry.type === GEOMETRY_TYPE.POLYGON
            ? feature.geometry.coordinates.flat(1)
            : feature.geometry.type === GEOMETRY_TYPE.POINT
              ? [feature.geometry.coordinates]
              : feature.geometry.coordinates.flat(2);

        coordinates.forEach(coordinate => {
          if (Array.isArray(coordinate) && coordinate.length === 2) {
            bounds.extend([coordinate.at(0), coordinate.at(-1)]);
          }
        });
      }
    });

    if (!bounds.isValid()) return;

    const center = bounds.getCenter();

    map?.setView(center, Math.min(map?.getBoundsZoom(bounds), ZOOM_MAX));
  }

  const { farms, geofences, sites } = useFeatures({ mapData });

  async function tick() {
    const validTrackingFeatures = tracking?.features?.filter(t => t.geometry.coordinates.every(c => c !== null)) || [];
    const updatedTrackingFeatures = addStartEndIcons({ ...tracking, features: validTrackingFeatures });

    calculateBounds(updatedTrackingFeatures);

    setTracks({ ...tracking, features: updatedTrackingFeatures });
    setState(prev => ({
      ...prev,
      features: geofences,
      features_master: farms,
      features_sites: sites
    }));
  }

  return (
    <LayersControl position={LAYERS_CONTROL_POSITION}>
      <BaseLayers />
      <CustomLayers
        features={state.features}
        features_master={state.features_master}
        features_sites={state.features_sites}
        grazing={grazing}
      />

      <Overlay checked name={`Current ${LAYER_NAME.LIVESTOCK}`}>
        <FeatureGroup edit={{ edit: false, delete: false }}>
          {tracks &&
            tracks.features?.length > 0 &&
            tracks.features?.map(feature => {
              if (feature?.properties?.isVisible || feature?.geometry?.type === GEOMETRY_TYPE.LINE) {
                return (
                  <MapTrackingFeature
                    color={DEFAULT_LINE_COLOR}
                    geometry={feature.geometry?.coordinates}
                    key={feature.properties?.uniqueId}
                    properties={feature.properties}
                    type={feature.geometry?.type}
                    setAnimal={onAnimalClick}
                  />
                );
              }
            })}
        </FeatureGroup>

        {animal && isFullScreen && <AnimalDetails animal={animal} />}
      </Overlay>
    </LayersControl>
  );
}

function Map({ animal, grazing, mapData, tracking, onAnimalClick }) {
  return (
    <AppMapContainer height={400}>
      <TrackingLayer animal={animal} mapData={mapData} tracking={tracking} onAnimalClick={onAnimalClick} />
    </AppMapContainer>
  );
}

export default memo(Map);
